import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAuthActionParams } from '../../utils/authActionParams';

const CompletePasswordResetPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const { mode, oobCode } = useMemo(
    () => getAuthActionParams(location.search, location.hash),
    [location.search, location.hash]
  );

  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) {
      setVerifying(false);
      setLinkError(t('auth.completeReset.invalidLink'));
      return;
    }

    let cancelled = false;
    setVerifying(true);
    setLinkError('');

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        if (!cancelled) {
          setAccountEmail(email);
          setVerifying(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.code || '';
        if (code === 'auth/expired-action-code') {
          setLinkError(t('auth.completeReset.expiredLink'));
        } else {
          setLinkError(t('auth.completeReset.invalidLink'));
        }
        setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.completeReset.mismatch'));
      return;
    }

    if (!oobCode) {
      setError(t('auth.completeReset.invalidLink'));
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/expired-action-code') {
        setError(t('auth.completeReset.expiredLink'));
      } else if (code === 'auth/weak-password') {
        setError(t('auth.completeReset.weakPassword'));
      } else {
        setError(t('auth.completeReset.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 py-6">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        <div className="flex justify-center">
          <img
            src="/rentalyzer-logo.png"
            alt="Rentalyzer logo"
            className="h-24 sm:h-48 w-auto"
          />
        </div>
        <div className="p-5 sm:p-8 space-y-6 bg-white rounded shadow">
          <h2 className="text-xl sm:text-2xl font-bold text-center">{t('auth.completeReset.title')}</h2>
          {accountEmail && !success && (
            <p className="text-sm text-center text-gray-600">
              {t('auth.completeReset.subtitle').replace('{email}', accountEmail)}
            </p>
          )}
          {linkError && <div className="p-2 text-red-600 bg-red-100 rounded">{linkError}</div>}
          {error && <div className="p-2 text-red-600 bg-red-100 rounded">{error}</div>}
          {success && (
            <div className="p-2 text-green-600 bg-green-100 rounded">
              {t('auth.completeReset.success')}
            </div>
          )}
          {verifying && (
            <p className="text-sm text-center text-gray-600">{t('loading')}</p>
          )}
          {!verifying && !success && accountEmail && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">{t('auth.completeReset.newPassword')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">{t('auth.completeReset.confirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? t('loading') : t('auth.completeReset.submit')}
              </button>
            </form>
          )}
          {(success || linkError) && (
            <div className="text-sm text-center">
              <Link to="/login" className="text-blue-600 hover:underline">
                {t('auth.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompletePasswordResetPage;
